import { Theme } from "@mui/material";
import type {
  Annotations,
  Layout as PlotlyLayout,
  Shape,
  XAxisName,
  YAxisName,
} from "plotly.js";
import { RecipeImpurityStats } from "src/store/api/generatedApi";

export const toImpuritiesConstraint = (
  stat: RecipeImpurityStats
): ImpuritiesConstraint => ({
  name: stat.chemical_constraint_name,
  mean: stat.mean,
  std: stat.std,
  quantiles: stat.quantiles,
  weightPercentMinTolerance: stat.weight_percent_min,
  weightPercentMaxTolerance: stat.weight_percent_max,
  expectedOverToleranceRatePercent: stat.expected_over_tolerance_rate * 100,
  expectedUnderToleranceRatePercent: stat.expected_under_tolerance_rate * 100,
  expectedFailureRatePercent:
    (stat.expected_over_tolerance_rate + stat.expected_under_tolerance_rate) *
    100,
});
export type ImpuritiesConstraint = {
  name: string;
  quantiles: Record<number, number>;
  weightPercentMinTolerance: number | null;
  weightPercentMaxTolerance: number;
  expectedOverToleranceRatePercent: number;
  expectedUnderToleranceRatePercent: number;
  expectedFailureRatePercent: number;
} & Partial<Pick<RecipeImpurityStats, "mean" | "std">>;

export interface ImpuritiesPlotProps {
  constraints: ImpuritiesConstraint[];
  height?: number;
}

// Plotly accepts dynamically numbered axes beyond its finite TypeScript unions.
export const getXAxisId = (i: number): XAxisName => {
  return i === 0 ? "x" : (`x${i + 1}` as XAxisName);
};

export const getYAxisId = (i: number): YAxisName => {
  return i === 0 ? "y" : (`y${i + 1}` as YAxisName);
};

export const DEFAULT_IMPURITIES_PLOT_HEIGHT = 330;
export const MAX_COLUMNS_PER_ROW = 6;
const TITLE_OFFSET_CONSTANT = 1.03;

export const generateBasePlotLayoutStructure = ({
  constraints,
  theme,
  getMaxValueRendered,
  height = DEFAULT_IMPURITIES_PLOT_HEIGHT,
}: {
  constraints: ImpuritiesConstraint[];
  theme: Theme;
  getMaxValueRendered: (constraint: ImpuritiesConstraint) => number;
  height?: number;
}): Partial<PlotlyLayout> => {
  const columnsPerRow = Math.min(constraints.length, MAX_COLUMNS_PER_ROW);
  const rows = Math.ceil(constraints.length / columnsPerRow);

  const titleAnnotations: Partial<Annotations>[] = constraints.map(
    (constraint, i) => {
      const xAxisId = getXAxisId(i);

      const currentRow = Math.floor(i / columnsPerRow);
      // Paper coordinates span all rows; the offset keeps titles above each row.
      const yPosition = 1 - TITLE_OFFSET_CONSTANT * (currentRow / rows);

      return {
        text: constraint.name,
        x: 0,
        y: yPosition,
        xref: xAxisId,
        yref: "paper",
        showarrow: false,
        xanchor: "center",
        yanchor: "bottom",
        font: { size: 16 },
      };
    }
  );

  const toleranceLineShapes: Partial<Shape>[] = constraints.flatMap(
    (constraint, i) => {
      const xAxisId = getXAxisId(i);
      const yAxisId = getYAxisId(i);

      const maxLine: Partial<Shape> = {
        type: "line",
        x0: -0.4,
        x1: 0.4,
        y0: constraint.weightPercentMaxTolerance,
        y1: constraint.weightPercentMaxTolerance,
        xref: xAxisId,
        yref: yAxisId,
        line: { color: theme.palette.text.primary, width: 2 },
      };

      const hasMin =
        constraint.weightPercentMinTolerance !== null &&
        constraint.weightPercentMinTolerance > 0;
      if (hasMin) {
        const minLine: Partial<Shape> = {
          type: "line" as const,
          x0: -0.4,
          x1: 0.4,
          y0: constraint.weightPercentMinTolerance,
          y1: constraint.weightPercentMinTolerance,
          xref: xAxisId,
          yref: yAxisId,
          line: {
            color: theme.palette.text.primary,
            width: 2,
            dash: "dot",
          },
        };
        return [maxLine, minLine];
      }
      return [maxLine];
    }
  );

  const layout: Partial<PlotlyLayout> = {
    height: height * rows,
    showlegend: false,
    margin: { t: 40, l: 35, r: 0, b: 20 },
    grid: {
      rows: rows,
      columns: columnsPerRow,
      pattern: "independent",
      xgap: 0.15,
      ygap: 0.1,
    },
    plot_bgcolor: theme.palette.grey[100],
    annotations: titleAnnotations,
    shapes: toleranceLineShapes,
    autosize: true,
  };

  constraints.forEach((constraint, i) => {
    const maxValue = getMaxValueRendered(constraint);
    const yAxisMax =
      Math.max(
        maxValue,
        constraint.weightPercentMaxTolerance,
        constraint.weightPercentMinTolerance ?? 0,
        1e-5
      ) * 1.1;

    const numDecimalPlaces =
      yAxisMax > 1e-5
        ? Math.max(0, Math.trunc(1 - Math.floor(Math.log10(yAxisMax))))
        : 0;
    const tickFormatString = `.${numDecimalPlaces}f`;

    // Plotly supports numbered axis keys beyond those represented by Layout.
    const yAxisKey = i === 0 ? "yaxis" : (`yaxis${i + 1}` as "yaxis");
    layout[yAxisKey] = {
      range: [0, yAxisMax],
      gridcolor: theme.palette.background.default,
      zerolinecolor: theme.palette.background.default,
      tickformat: tickFormatString,
    };
  });

  return layout;
};
